import React, { FC } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { StepTemplate } from '../../components/styled/stepTemplate/StepTemplate'
import { RealEstateType } from '../../@types/realEstateType'
import { updateRealEstateType } from '../../store/lead/leadSlice'
import { ReportViewEventArgs } from '../../tracking/tracking'
import { Progress } from '../../@types/progress'
import { reportTrackingEvent } from '../../tracking/thunks'
import { Button } from '../../components/styled/Button/Button'
import { RealEstateTypeIcon } from '../../components/icons/StepIcons/RealEstateTypeIcon'
import { useReportViewEvent, useResponsive } from '../../hooks'
import './RealEstateTypeStep.scss'
import { WizardFlowType } from '../../@types/wizardFlowType'
import { IntegrationType } from '../../@types/config'
import { ReduxState } from '../../store/reducers'

interface RealEstateTypeStepProps {
  goToNextStep: () => unknown
  progress: Progress
}

const DEFAULT_HEADLINE = 'Um welche Immobilie handelt es sich?'

export const realEstateTypeButtons = {
  [RealEstateType.HOUSE]: 'Haus',
  [RealEstateType.APARTMENT]: 'Wohnung',
  [RealEstateType.LAND]: 'Grundstück',
  [RealEstateType.COMMERCIAL]: 'Gewerbe',
}

export const RealEstateTypeStep: FC<RealEstateTypeStepProps> = (props) => {
  const { isMobile } = useResponsive()
  const { config, flow } = useSelector((state: ReduxState) => state)
  const reportViewEventArgs: ReportViewEventArgs | undefined = !config.tealium
    ? undefined
    : location.pathname === '/maklervergleich/'
    ? { pageTitle: 'maklervergleich' }
    : location.pathname == '/immobilie-bewerten/'
    ? {
        pageTitle: 'immobilie-bewerten',
        customDimensions: {
          svc_module_name: 'ibw_realtor-leadengine',
        },
      }
    : undefined
  useReportViewEvent(reportViewEventArgs)
  const { theme, integrationType, isIframe } = config
  const { wizardFlowType } = flow
  const externalIntegration = integrationType === IntegrationType.EXTERNAL
  const isIv24Integration = integrationType === IntegrationType.IMMOVERKAUF24

  const dispatch = useDispatch()

  const goToNextStep = (realEstateType: RealEstateType) => {
    dispatch(updateRealEstateType(realEstateType))
    dispatch(
      reportTrackingEvent({
        pageTitle: 'realestatetype',
        label: 'realestatetype_submit',
        inputValue: realEstateType,
        eventName: 'lead_input',
        customDimensions: {
          ga_cd_lead_engine_input_value: realEstateType,
        },
      })
    )

    props.goToNextStep()
  }

  return (
    <>
      {isIframe && !isIv24Integration && (
        <div
          className='grid grid-flex grid-justify-center iframe-intro'
          style={{ paddingTop: 20, backgroundColor: theme?.background !== '' ? theme.background : 'inherit' }}
        >
          <h2
            className={`align-center margin-bottom-xxl ${isIv24Integration ? `iframe-heading` : ``}`}
            data-testid='iframe-heading'
            style={{ color: theme?.text !== '' ? theme.text : 'inherit' }}
          >
            {wizardFlowType === WizardFlowType.RLE
              ? `${
                  config.wordings.realEstate.headline || config.wordings.realEstate.headline === ''
                    ? config.wordings.realEstate.headline
                    : `Immobilie erfolgreich verkaufen mit einem Makler`
                }`
              : `${
                  config.wordings.realEstate.headline || config.wordings.realEstate.headline === ''
                    ? config.wordings.realEstate.headline
                    : `Schnell und bequem zur Immobilienbewertung`
                }`}
          </h2>
        </div>
      )}
      <StepTemplate
        headline={DEFAULT_HEADLINE}
        icon={<RealEstateTypeIcon />}
        progress={props.progress}
        displayNext={false}
        backgroundColor={theme.background}
        textColor={theme.text}
        secondaryLight={theme.secondaryLight}
      >
        {Object.entries(realEstateTypeButtons).map(([realEstateType, realEstateTypeLabel]) => (
          <Button
            key={realEstateType}
            onClick={() => goToNextStep(realEstateType as RealEstateType)}
            className={`margin-right-l margin-bottom-xl ${isIv24Integration ? `iframe-heading` : ``}`}
            text={realEstateTypeLabel}
            backgroundColor={
              externalIntegration || isIv24Integration
                ? theme.background !== ''
                  ? theme?.secondary
                  : undefined
                : theme.background
            }
            color={externalIntegration || isIv24Integration ? '#FFFFFF' : theme.text}
            borderColor={theme.secondary}
            fontWeight={isIframe && externalIntegration ? `bold` : ''}
          />
        ))}
        {isIframe && !isIv24Integration && (
          <div className={`grid grid-flex grid-justify-space-around margin-top-xl`}>
            {isMobile ? (
              <div className='grid grid-flex grid-justify-space-around margin-top-xl'>
                <div className='grid-item'>
                  {config.wordings.realEstate.bulletPointOne !== '' ? (
                    <div className='grid grid-flex gutter-s real-estate-bulletpoint'>
                      <div className='grid-item'>
                        <i
                          className='font-xl is24-icon-positive-check'
                          style={{ color: theme?.primary ? theme.primary : '#00d9b1' }}
                        ></i>
                      </div>
                      <div className='grid-item' data-testid='bullet-point1'>
                        {config.wordings.realEstate.bulletPointOne ??
                          `${
                            wizardFlowType === WizardFlowType.RLE
                              ? '100% kostenlos & unverbindlich'
                              : 'Analyse per E-Mail'
                          }`}
                      </div>
                    </div>
                  ) : null}
                  {config.wordings.realEstate.bulletPointTwo !== '' ? (
                    <div className='grid grid-flex gutter-s real-estate-bulletpoint'>
                      <div className='grid-item'>
                        <i
                          className='font-xl is24-icon-positive-check  '
                          style={{ color: theme?.primary ? theme.primary : '#00d9b1' }}
                        ></i>
                      </div>
                      <div className='grid-item ' data-testid='bullet-point2'>
                        {config.wordings.realEstate.bulletPointTwo ??
                          `${
                            wizardFlowType === WizardFlowType.RLE
                              ? 'Zeit sparen beim Verkauf'
                              : 'In nur 3 Minuten fertig'
                          }`}
                      </div>
                    </div>
                  ) : null}
                  {config.wordings.realEstate.bulletPointThree !== '' ? (
                    <div className='grid grid-flex gutter-s real-estate-bulletpoint'>
                      <div className='grid-item'>
                        <i
                          className='font-xl is24-icon-positive-check'
                          style={{ color: theme?.primary ? theme.primary : '#00d9b1' }}
                        ></i>
                      </div>
                      <div className='grid-item' data-testid='bullet-point3'>
                        {config.wordings.realEstate.bulletPointThree ??
                          `${
                            wizardFlowType === WizardFlowType.RLE
                              ? 'Passende Makler deutschlandweit'
                              : '100% kostenlos & unverbindlich'
                          }`}
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>
            ) : (
              <div className={`grid grid-flex grid-justify-space-around margin-top-xl`}>
                <div className='grid-item desk-nine-twelfths lap-grid-wrapper' data-testid='contact-step-footer'>
                  {config.wordings.realEstate.bulletPointOne !== '' ? (
                    <div className={`grid-item desk-one-third lap-grid-wrapper real-estate-bulletpoint`}>
                      <div className='grid grid-flex gutter-s'>
                        <div className='grid-item lap-grid-wrapper '>
                          <i
                            className='font-xl is24-icon-positive-check'
                            style={{ color: theme.primary ?? '#00d9b1' }}
                          />
                        </div>
                        <div className='grid-item lap-grid-wrapper'>
                          {config.wordings.realEstate.bulletPointOne ??
                            `${
                              wizardFlowType === WizardFlowType.RLE
                                ? '100% kostenlos & unverbindlich'
                                : 'Analyse per E-Mail'
                            }`}
                        </div>
                      </div>
                    </div>
                  ) : null}
                  {config.wordings.realEstate.bulletPointTwo !== '' ? (
                    <div className='grid-item desk-one-third lap-grid-wrapper real-estate-bulletpoint'>
                      <div className='grid grid-flex gutter-s '>
                        <div className='grid-item lap-grid-wrapper '>
                          <i
                            className='font-xl is24-icon-positive-check'
                            style={{ color: theme?.primary ? theme.primary : '#00d9b1' }}
                          />
                        </div>
                        <div className='grid-item lap-grid-wrapper'>
                          {config.wordings.realEstate.bulletPointTwo ??
                            `${
                              wizardFlowType === WizardFlowType.RLE
                                ? 'Zeit sparen beim Verkauf'
                                : 'In nur 3 Minuten fertig'
                            }`}
                        </div>
                      </div>
                    </div>
                  ) : null}
                  {config.wordings.realEstate.bulletPointThree !== '' ? (
                    <div className='grid-item desk-one-third lap-grid-wrapper real-estate-bulletpoint'>
                      <div className='grid grid-flex gutter-s'>
                        <div className='grid-item lap-grid-wrapper'>
                          <i
                            className='font-xl is24-icon-positive-check'
                            style={{ color: theme?.primary ? theme.primary : '#00d9b1' }}
                          />
                        </div>
                        <div className='grid-item lap-grid-wrapper'>
                          {config.wordings.realEstate.bulletPointThree ??
                            `${
                              wizardFlowType === WizardFlowType.RLE
                                ? 'Passende Makler deutschlandweit'
                                : '100% kostenlos & unverbindlich'
                            }`}
                        </div>
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>
            )}
          </div>
        )}
      </StepTemplate>
    </>
  )
}

export default RealEstateTypeStep
